import {
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { VegaEmbed } from "react-vega";
import type { View } from "vega";
import type { EmbedOptions, Result } from "vega-embed";
import type { TopLevelSpec } from "vega-lite";
import { mergeConfig } from "../theme/config.js";
import { useVizTheme, type UseVizThemeOptions } from "../theme/useVizTheme.js";
import type { ChartData } from "../types.js";

export interface VegaChartProps {
  /** The Vega-Lite spec to render. The themed config is merged in automatically. */
  spec: TopLevelSpec;
  /** Optional inline data; overrides `spec.data` when provided. */
  data?: ChartData;
  /** Plot height in pixels. Width is responsive to the container by default. */
  height?: number;
  /** Plot width — defaults to `"container"` (fills and tracks the parent width). */
  width?: number | "container";
  /** Vega renderer. SVG (default) is crisp and theme-able; canvas is faster for large data. */
  renderer?: "svg" | "canvas";
  /** Show Vega's export/source actions menu. Off by default. */
  actions?: boolean;
  /** Show hover tooltips. On by default. */
  tooltip?: boolean;
  /** Theme overrides (custom palette, font, or token root element). */
  theme?: UseVizThemeOptions;
  className?: string;
  style?: CSSProperties;
  /** Called with the Vega {@link View} once the chart has embedded. */
  onNewView?: (view: View) => void;
  /** Called if embedding fails. */
  onError?: (error: unknown) => void;
  /** Rendered in place of the chart when embedding fails. */
  errorFallback?: ReactNode;
}

const DEFAULT_HEIGHT = 300;

/**
 * Low-level chart renderer. Resolves the active theme, merges it into the
 * spec's config, and embeds the chart responsively. Most callers should reach
 * for the high-level components (`BarChart`, `LineChart`, …), but this is the
 * escape hatch for hand-written Vega-Lite specs.
 */
export function VegaChart({
  spec,
  data,
  height = DEFAULT_HEIGHT,
  width = "container",
  renderer = "svg",
  actions = false,
  tooltip = true,
  theme,
  className,
  style,
  onNewView,
  onError,
  errorFallback,
}: VegaChartProps): ReactNode {
  const { config, isDark } = useVizTheme(theme);
  const [error, setError] = useState<unknown>(null);

  const mergedSpec = useMemo<TopLevelSpec>(() => {
    const withData = data ? { ...spec, data: { values: [...data] } } : spec;
    return {
      width,
      height,
      ...withData,
      config: mergeConfig(config, withData.config),
    };
  }, [spec, data, config, width, height]);

  const options = useMemo<EmbedOptions>(
    () => ({
      renderer,
      actions,
      tooltip: tooltip ? { theme: isDark ? "dark" : "light" } : false,
    }),
    [renderer, actions, tooltip, isDark],
  );

  const handleEmbed = useCallback(
    (result: Result) => {
      setError(null);
      onNewView?.(result.view);
    },
    [onNewView],
  );

  const handleError = useCallback(
    (err: unknown) => {
      setError(err);
      onError?.(err);
    },
    [onError],
  );

  if (error) {
    return (
      <div
        className={["erp-viz-chart", "erp-viz-chart--error", className]
          .filter(Boolean)
          .join(" ")}
        style={{ width: "100%", ...style }}
        role="alert"
      >
        {errorFallback ?? (
          <span className="erp-viz-error-text">Unable to render chart.</span>
        )}
      </div>
    );
  }

  return (
    <VegaEmbed
      className={["erp-viz-chart", className].filter(Boolean).join(" ")}
      style={{ width: "100%", ...style }}
      spec={mergedSpec}
      options={options}
      onEmbed={handleEmbed}
      onError={handleError}
    />
  );
}
